import { useChatClass } from "../../theme";
import { Avatar } from "../Avatar";
import { Icon, IconProps } from "../Icon";

import { Button, Container, Title } from "./styled";

export interface HeaderActionProps {
  /** The name of the SVG icon that will be used for the button  */
  icon: IconProps["name"];
  /** The size of the SVG icon that will be used for the button  */
  size: IconProps["size"];
  color?: IconProps["color"];
  /** A callback that is executed when the button is clicked */
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

export interface HeaderProps {
  /** The name of your assistant or title of the conversation.*/
  title: string;
  /** An image URL that will be rendered as a small {@link Avatar}. */
  image: string;
  /** A list of actions that will appear as icon buttons. */
  actions?: HeaderActionProps[];
}

export interface HeaderComponent extends React.FC<HeaderProps> {
  Container: typeof Container;
  Button: typeof Button;
  Title: typeof Title;
}

/**  Header for the chat widget with image, title and controls. */
const Header: HeaderComponent = ({
  title,
  image,
  actions = [],
}: HeaderProps) => {
  const chatClass = useChatClass();
  // Helper function to get default aria label from icon name
  const getDefaultAriaLabel = (iconName: string): string => {
    switch (iconName) {
      case "minus":
        return "Minimize chat";
      case "close":
        return "Close chat";
      case "refresh":
        return "Restart chat";
      default:
        return "Action button";
    }
  };

  return (
    <Container className={chatClass("header")}>
      <Avatar avatar={image} size="large" />
      <Title id="chat-widget-title">{title}</Title>
      {actions.map(({ icon, size, onClick, ariaLabel }) => {
        return (
          <Button
            onClick={onClick}
            key={icon}
            aria-label={ariaLabel || getDefaultAriaLabel(icon)}
            type="button"
          >
            <Icon name={icon} size={size} aria-hidden="true" />
          </Button>
        );
      })}
    </Container>
  );
};

Header.Container = Container;
Header.Button = Button;
Header.Title = Title;

export { Header };
